import { useCallback } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  getCalculation,
  createCalculation,
  updateCalculation,
  getRules,
  addRule,
  updateRule,
  deleteRule,
  validateCeebCodes,
  getGetCalculationQueryKey,
  getGetRulesQueryKey,
} from 'api/generated/aid-simulation-calculation-exceptions/aid-simulation-calculation-exceptions';
import type {
  AidModelCalculationDto,
  AidModelCalculationRequest,
  CalculationRuleDto,
  CalculationRuleRequest,
  CeebCodesDto,
} from 'api/generated/schemas';

type ModelId = string | number;
type CalculationId = string | number;
type RuleId = string | number;

const toNumber = (value: ModelId): number => Number(value);

interface UseCalculationParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface UseCalculationRulesParams {
  userId: string;
  modelId: ModelId;
  calculationId: CalculationId;
  enabled?: boolean;
}

interface CreateCalculationVars {
  userId: string;
  modelId: ModelId;
  data: AidModelCalculationRequest;
}

interface UpdateCalculationVars {
  userId: string;
  modelId: ModelId;
  id: CalculationId;
  data: AidModelCalculationRequest;
}

interface AddCalculationRulesVars {
  userId: string;
  modelId: ModelId;
  calculationId: CalculationId;
  requests: CalculationRuleRequest[];
}

interface UpdateCalculationRuleVars {
  userId: string;
  modelId: ModelId;
  calculationId: CalculationId;
  ruleId: RuleId;
  data: CalculationRuleRequest;
}

interface DeleteCalculationRuleVars {
  userId: string;
  modelId: ModelId;
  calculationId: CalculationId;
  ruleId: RuleId;
}

interface ValidateCeebCodesVars {
  userId: string;
  modelId: ModelId;
  ceebCodes: string[];
}

const EMPTY_CEEB_CODES_DTO: CeebCodesDto = { valid: [], invalid: [] };

const runValidateCeebCodes = (
  userId: string,
  modelId: ModelId,
  ceebCodes: string[]
): Promise<CeebCodesDto> =>
  validateCeebCodes(userId, toNumber(modelId), { ceebCodes }).then(
    (response) => response.ceebCodes ?? EMPTY_CEEB_CODES_DTO
  );

const getCeebCodesValidationQueryKey = (userId: string, modelId: ModelId) =>
  ['ceebCodesValidation', userId, toNumber(modelId)] as const;

export { getGetCalculationQueryKey, getGetRulesQueryKey };

export const useCalculation = ({
  userId,
  modelId,
  enabled = true,
}: UseCalculationParams) =>
  useQuery<AidModelCalculationDto>({
    queryKey: getGetCalculationQueryKey(userId, toNumber(modelId)),
    queryFn: () => getCalculation(userId, toNumber(modelId)),
    enabled: enabled && !!userId && !!modelId,
    retry: false,
  });

export const useCreateCalculation = () => {
  const queryClient = useQueryClient();

  return useMutation<AidModelCalculationDto, unknown, CreateCalculationVars>({
    mutationFn: ({ userId, modelId, data }) =>
      createCalculation(userId, toNumber(modelId), data),
    onSuccess: (_result, { userId, modelId }) => {
      queryClient.invalidateQueries({
        queryKey: getGetCalculationQueryKey(userId, toNumber(modelId)),
      });
    },
  });
};

export const useUpdateCalculation = () => {
  const queryClient = useQueryClient();

  return useMutation<AidModelCalculationDto, unknown, UpdateCalculationVars>({
    mutationFn: ({ userId, modelId, id, data }) =>
      updateCalculation(userId, toNumber(modelId), toNumber(id), data),
    onSuccess: (_result, { userId, modelId }) => {
      queryClient.invalidateQueries({
        queryKey: getGetCalculationQueryKey(userId, toNumber(modelId)),
      });
    },
  });
};

export const useCalculationRules = ({
  userId,
  modelId,
  calculationId,
  enabled = true,
}: UseCalculationRulesParams) =>
  useQuery<CalculationRuleDto[]>({
    queryKey: getGetRulesQueryKey(
      userId,
      toNumber(modelId),
      toNumber(calculationId)
    ),
    queryFn: () => getRules(userId, toNumber(modelId), toNumber(calculationId)),
    enabled: enabled && !!userId && !!modelId && !!calculationId,
  });

export const useAddCalculationRules = () => {
  const queryClient = useQueryClient();

  return useMutation<CalculationRuleDto[], unknown, AddCalculationRulesVars>({
    mutationFn: async ({ userId, modelId, calculationId, requests }) => {
      const created: CalculationRuleDto[] = [];

      for (const data of requests) {
        created.push(
          await addRule(
            userId,
            toNumber(modelId),
            toNumber(calculationId),
            data
          )
        );
      }

      return created;
    },
    onSuccess: (_result, { userId, modelId, calculationId }) => {
      queryClient.invalidateQueries({
        queryKey: getGetRulesQueryKey(
          userId,
          toNumber(modelId),
          toNumber(calculationId)
        ),
      });
      queryClient.invalidateQueries({
        queryKey: getCeebCodesValidationQueryKey(userId, modelId),
      });
    },
  });
};

interface RulesMutationContext {
  previousRules?: CalculationRuleDto[];
}

export const useUpdateCalculationRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CalculationRuleDto,
    unknown,
    UpdateCalculationRuleVars,
    RulesMutationContext
  >({
    mutationFn: ({ userId, modelId, calculationId, ruleId, data }) =>
      updateRule(
        userId,
        toNumber(modelId),
        toNumber(calculationId),
        toNumber(ruleId),
        data
      ),

    onMutate: async ({ userId, modelId, calculationId, ruleId, data }) => {
      const queryKey = getGetRulesQueryKey(
        userId,
        toNumber(modelId),
        toNumber(calculationId)
      );

      await queryClient.cancelQueries({ queryKey });

      const previousRules =
        queryClient.getQueryData<CalculationRuleDto[]>(queryKey);

      queryClient.setQueryData<CalculationRuleDto[]>(queryKey, (old) =>
        (old ?? []).map((rule) =>
          String(rule.id) === String(ruleId) ? { ...rule, ...data } : rule
        )
      );

      return { previousRules };
    },
    onError: (_error, { userId, modelId, calculationId }, context) => {
      if (context?.previousRules) {
        queryClient.setQueryData(
          getGetRulesQueryKey(
            userId,
            toNumber(modelId),
            toNumber(calculationId)
          ),
          context.previousRules
        );
      }
    },
    onSettled: (_result, _error, { userId, modelId, calculationId }) => {
      queryClient.invalidateQueries({
        queryKey: getGetRulesQueryKey(
          userId,
          toNumber(modelId),
          toNumber(calculationId)
        ),
      });
      queryClient.invalidateQueries({
        queryKey: getCeebCodesValidationQueryKey(userId, modelId),
      });
    },
  });
};

/** Backs the CEEB codes chip input's per-batch matching call (FR-13/FR-14). */
export const useValidateCeebCodes = () =>
  useMutation<CeebCodesDto, unknown, ValidateCeebCodesVars>({
    mutationFn: ({ userId, modelId, ceebCodes }) =>
      runValidateCeebCodes(userId, modelId, ceebCodes),
  });

/** Forces the table's cached CEEB validation results to refetch on demand. */
export const useInvalidateCeebCodesValidation = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (userId: string, modelId: ModelId) =>
      queryClient.invalidateQueries({
        queryKey: getCeebCodesValidationQueryKey(userId, modelId),
      }),
    [queryClient]
  );
};

export interface CeebCodesValidationItem {
  /** Correlates a result back to its rule; not sent to the backend. */
  id: string;
  ceebCodes: string[];
}

/**
 * Re-validates one CEEB rule's stored codes per list item (FR-29/FR-30): the
 * backend doesn't persist match status, so the table's matched/unmatched counts
 * are re-derived the same way the modal derives them, one query per rule.
 */
export const useCeebCodesValidationQueries = (
  userId: string,
  modelId: ModelId,
  items: CeebCodesValidationItem[]
) =>
  useQueries({
    queries: items.map(({ id, ceebCodes }) => ({
      queryKey: [
        ...getCeebCodesValidationQueryKey(userId, modelId),
        id,
        ceebCodes.join(','),
      ],
      queryFn: () => runValidateCeebCodes(userId, modelId, ceebCodes),
      enabled: !!userId && !!modelId && !!ceebCodes.length,
    })),
  });

export const useDeleteCalculationRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    DeleteCalculationRuleVars,
    RulesMutationContext
  >({
    mutationFn: ({ userId, modelId, calculationId, ruleId }) =>
      deleteRule(
        userId,
        toNumber(modelId),
        toNumber(calculationId),
        toNumber(ruleId)
      ),
    onMutate: async ({ userId, modelId, calculationId, ruleId }) => {
      const queryKey = getGetRulesQueryKey(
        userId,
        toNumber(modelId),
        toNumber(calculationId)
      );

      await queryClient.cancelQueries({ queryKey });

      const previousRules =
        queryClient.getQueryData<CalculationRuleDto[]>(queryKey);

      queryClient.setQueryData<CalculationRuleDto[]>(queryKey, (old) =>
        (old ?? []).filter((rule) => String(rule.id) !== String(ruleId))
      );

      return { previousRules };
    },
    onError: (_error, { userId, modelId, calculationId }, context) => {
      if (context?.previousRules) {
        queryClient.setQueryData(
          getGetRulesQueryKey(
            userId,
            toNumber(modelId),
            toNumber(calculationId)
          ),
          context.previousRules
        );
      }
    },
    onSettled: (_result, _error, { userId, modelId, calculationId }) => {
      queryClient.invalidateQueries({
        queryKey: getGetRulesQueryKey(
          userId,
          toNumber(modelId),
          toNumber(calculationId)
        ),
      });
    },
  });
};
